import * as Yup from "yup";
import { CircuitAggregationType, CircuitCategory, CircuitInputType, SortType, type CircuitAdd } from "types/circuit";
import { markdownEnabled } from "config/features";

/** The "Neutral" ranking choice — maps to a `null` `metricSorting` (PB disabled). */
export const CIRCUIT_RANKING_NEUTRAL = "Neutral";

export type CircuitRanking = SortType | typeof CIRCUIT_RANKING_NEUTRAL;

/** Bounds for the "Intervals to complete" (`roundCount`) field. */
export const CIRCUIT_ROUND_COUNT_MIN = 1;
export const CIRCUIT_ROUND_COUNT_MAX = 20;

/** Maximum lengths mirrored in the Yup schema and the field helper text. */
export const CIRCUIT_NAME_MAX_LENGTH = 200;
export const CIRCUIT_INSTRUCTIONS_MAX_LENGTH = 3000;

/**
 * Shape of the create-circuit form. The `""` sentinels represent an unmade single
 * selection; they are rejected by the schema (where required) and never reach the API.
 * The server generates the circuit's metric arrays, so the form only holds the
 * high-level inputs of {@link CircuitAdd}.
 */
export interface CircuitFormValues {
  name: string;
  category: CircuitCategory | "";
  instructions: string;
  roundCount: number | "";
  input: CircuitInputType;
  aggregation: CircuitAggregationType | "";
  ranking: CircuitRanking | "";
  videoId: string | null;
}

export const circuitFormInitialValues: CircuitFormValues = {
  name: "",
  category: "",
  instructions: "",
  roundCount: CIRCUIT_ROUND_COUNT_MIN,
  input: CircuitInputType.None,
  aggregation: "",
  ranking: "",
  videoId: null,
};

/** `None` (completion-only) circuits carry no aggregation/ranking. */
const isScoredInput = (input: CircuitInputType) => input !== CircuitInputType.None;

const rankingToMetricSorting = (ranking: CircuitRanking | ""): SortType | null =>
  ranking === SortType.HigherBetter || ranking === SortType.LowerBetter ? ranking : null;

/**
 * Maps the validated form values to the {@link CircuitAdd} create payload. Single
 * category becomes a one-element array; a `None` input clears aggregation/ranking; a
 * Neutral ranking becomes a `null` `metricSorting`.
 */
export function circuitFormValuesToCircuitAdd(values: CircuitFormValues): CircuitAdd {
  const scored = isScoredInput(values.input);

  return {
    name: values.name.trim(),
    instructions: markdownEnabled ? values.instructions : values.instructions.trim(),
    videoId: values.videoId,
    roundCount: Number(values.roundCount),
    input: values.input,
    aggregation: scored ? (values.aggregation as CircuitAggregationType) : null,
    metricSorting: scored ? rankingToMetricSorting(values.ranking) : null,
    categories: values.category ? [values.category] : [],
  };
}

export const circuitCreateSchema: Yup.ObjectSchema<CircuitFormValues> = Yup.object({
  name: Yup.string()
    .trim()
    .required("errors.circuit_name_required")
    .max(CIRCUIT_NAME_MAX_LENGTH, "errors.circuit_name_max_length"),
  category: Yup.string()
    .oneOf(Object.values(CircuitCategory), "errors.circuit_category_required")
    .required("errors.circuit_category_required"),
  instructions: markdownEnabled
    ? Yup.string()
        .required("errors.circuit_instructions_required")
        .test("not-blank", "errors.circuit_instructions_required", (value) => Boolean(value?.trim()))
        .max(CIRCUIT_INSTRUCTIONS_MAX_LENGTH, "errors.circuit_instructions_max_length")
    : Yup.string()
        .trim()
        .required("errors.circuit_instructions_required")
        .max(CIRCUIT_INSTRUCTIONS_MAX_LENGTH, "errors.circuit_instructions_max_length"),
  roundCount: Yup.number()
    .typeError("errors.circuit_round_count_range")
    .required("errors.circuit_round_count_range")
    .integer("errors.circuit_round_count_range")
    .min(CIRCUIT_ROUND_COUNT_MIN, "errors.circuit_round_count_range")
    .max(CIRCUIT_ROUND_COUNT_MAX, "errors.circuit_round_count_range"),
  input: Yup.mixed<CircuitInputType>()
    .oneOf(Object.values(CircuitInputType), "errors.circuit_input_required")
    .required("errors.circuit_input_required"),
  aggregation: Yup.mixed<CircuitAggregationType>().when("input", {
    is: isScoredInput,
    then: (schema) =>
      schema
        .oneOf(Object.values(CircuitAggregationType), "errors.circuit_aggregation_required")
        .required("errors.circuit_aggregation_required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  ranking: Yup.mixed<CircuitRanking>()
    .when("input", {
      is: isScoredInput,
      then: (schema) =>
        schema
          .oneOf(
            [SortType.HigherBetter, SortType.LowerBetter, CIRCUIT_RANKING_NEUTRAL],
            "errors.circuit_ranking_required"
          )
          .required("errors.circuit_ranking_required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .test("best-not-neutral", "errors.circuit_best_neutral_conflict", function (value) {
      return !(this.parent.aggregation === CircuitAggregationType.Best && value === CIRCUIT_RANKING_NEUTRAL);
    }),
  videoId: Yup.string().nullable().defined().default(null),
}) as Yup.ObjectSchema<CircuitFormValues>;
